import * as React from "react";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { Checkbox } from "react-ui";

import { emptyRef } from "../../../../TypingUtils";
import { SummaryState } from "../TreePicklistItemProvider";
import { patchTreeStructure, updateIndeterminate } from "../patchTreeStructure";
import { TREE_PICKLIST_TOGGLE_ITEM, TREE_PICKLIST_TOGGLE_SUMMARY } from "../../../../metricsAction";
import { useMetrics } from "../../../../Internals/Metrics/MetricsProviderContext";
import { useAsyncEffect } from "../../../../../../../Common/UseAsyncEffect";
import { TreePicklistItem } from "../TreePicklistItem";

import { ArrowIcon, FieldSet, FlexNodeLabelContainer, MultipleLeafLabel, MultipleNodeLabel } from "./TreeNode.styles";
import { CommonTreeNodeProps } from "./utils/ITreeNodeProps";
import { useMultipleNodeLabelName } from "./utils/useNodeLabelName";
import { IResetNodeManager } from "./utils/useResetCallbacks";
import { useChildrenNodes } from "./utils/useChildrenNodes";
import { Preloader } from "./Preloader";

export interface MultipleTreeNodeProps extends CommonTreeNodeProps {
    parentState: SummaryState;
    nodesToSave: TreePicklistItem[];
    resetNodeStateManager: IResetNodeManager;

    recalculateParentState: (item: TreePicklistItem, newState: SummaryState) => void;
}

export const MultipleTreeNode = (props: MultipleTreeNodeProps) => {
    const { item } = props;
    const summaryRef = useRef<Checkbox>(emptyRef);

    const [summaryValue, setSummaryValue] = useState<SummaryState>(SummaryState.Indeterminate);
    const [summaryOpened, setSummaryOpened] = useState(false);

    const labelName = useMultipleNodeLabelName(props.display, item);

    const [pickedChildren, setPickedChildren] = React.useState<TreePicklistItem[]>([]);

    const [indeterminatedChildren, setIndeterminatedChildren] = React.useState<TreePicklistItem[]>([]);

    const { children, loading } = useChildrenNodes(props, summaryOpened);

    const isCurrentNodeInNeedsToBeOpenedArray = useMemo(
        () => props.needNodesToOpen.some(itemToOpen => itemToOpen.elementId === item.elementId),
        [props.needNodesToOpen, props.item]
    );

    const track = useMetrics();

    useAsyncEffect(async () => {
        if (!isCurrentNodeInNeedsToBeOpenedArray) {
            return;
        }

        if (checkIsCurrentElementPicked()) {
            setNextValueForSummary(SummaryState.Checked);
        } else {
            setSummaryOpened(true);
        }
    }, [isCurrentNodeInNeedsToBeOpenedArray]);

    useAsyncEffect(async () => {
        if (checkIsCurrentElementPicked()) {
            setNextValueForSummary(SummaryState.Checked);
        } else if (checkHasPickedDescendant()) {
            setSummaryOpened(true);
        }
    }, []);

    const checkIsCurrentElementPicked = () =>
        props.nodesToSave.some(itemToOpen => itemToOpen.elementId === item.elementId);

    const checkHasPickedDescendant = () => props.nodesToSave.some(value => value.hasAncestorWithId(item.elementId));

    useEffect(() => {
        if (props.parentState === SummaryState.Indeterminate) {
            return;
        }
        if (props.parentState === SummaryState.Unchecked && summaryValue === SummaryState.Indeterminate) {
            return;
        }
        setNextValueForSummary(props.parentState);
    }, [props.parentState]);

    useEffect(() => {
        props.resetNodeStateManager.addResetCallback(resetStateFunction);
        return () => {
            props.resetNodeStateManager.removeResetCallback(resetStateFunction);
        };
    }, []);

    const resetStateFunction = useCallback(() => {
        setSummaryValue(SummaryState.Unchecked);
    }, []);

    const summaryCheckboxChangeHandle = (checked: boolean) => {
        track(TREE_PICKLIST_TOGGLE_ITEM, {
            value: item.toOriginal()[props.searchByField],
            state: checked,
            gid: props.gId,
        });
        setNextValueForSummary(checked ? SummaryState.Checked : SummaryState.Unchecked);
    };

    const clickOnLabelHandle = () => {
        const checked = !summaryRef?.current?.props.checked;
        summaryCheckboxChangeHandle(checked);
    };

    const setNextValueForSummary = (nextValue: SummaryState) => {
        setSummaryValue(nextValue);
        props.recalculateParentState(item, nextValue);
    };

    const toggleHandler = () => {
        setSummaryOpened(prevState => {
            track(TREE_PICKLIST_TOGGLE_SUMMARY, {
                value: item.toOriginal()[props.searchByField],
                state: !prevState,
                gid: props.gId,
            });
            return !prevState;
        });
    };

    const recalculateParentState = (treeItem: TreePicklistItem, newState: SummaryState) => {
        patchTreeStructure(treeItem, newState, setPickedChildren);
        updateIndeterminate(treeItem, newState, setIndeterminatedChildren);
    };

    useEffect(() => {
        props.updateNodesToSave(item, summaryValue);

        const isNodeStateDefined = summaryValue !== SummaryState.Indeterminate;
        const isSummaryClosed = !summaryOpened;
        if (isSummaryClosed && isNodeStateDefined) {
            removePickedDescendant();
        }
    }, [summaryValue]);

    // Если текущий элемент является родителем для какой-либо ноды из currentValue,
    // то при его переходе в состояние Checked или Unchecked, мы должны убрать из currentValue ноду.
    // Мы делаем это только для свернутых нод, чтобы развернутые потомки отображались с галочкой
    const removePickedDescendant = () => {
        props.nodesToSave.forEach(async value => {
            const hasChildPicked = value.hasAncestorWithId(item.elementId);
            if (hasChildPicked) {
                props.updateNodesToSave(value, SummaryState.Unchecked);
            }
        });
    };

    useEffect(() => {
        updateStateViaChildren();
    }, [pickedChildren, indeterminatedChildren]);

    const updateStateViaChildren = () => {
        if (!summaryOpened) {
            return;
        }
        summaryRef?.current?.resetIndeterminate();
        const pickedChildrenCount = pickedChildren.length;
        const indeterminetedChildrenCount = indeterminatedChildren.length;

        if (pickedChildrenCount === item.childrenCount) {
            setNextValueForSummary(SummaryState.Checked);
        } else if (pickedChildrenCount === 0 && indeterminetedChildrenCount === 0) {
            setNextValueForSummary(SummaryState.Unchecked);
        } else if (pickedChildrenCount < item.childrenCount || indeterminetedChildrenCount > 0) {
            setNextValueForSummary(SummaryState.Indeterminate);
            setTimeout(() => {
                summaryRef?.current?.setIndeterminate();
            });
        }
    };

    const showingPreloader = loading;

    const hasTreePicklistNodesToOpen = Boolean(props.needNodesToOpen.length);
    const shouldSkipNode = props.searchMode && hasTreePicklistNodesToOpen && !isCurrentNodeInNeedsToBeOpenedArray;
    if (shouldSkipNode) {
        return null;
    }

    const checkbox = (
        <Checkbox
            checked={summaryValue === SummaryState.Checked}
            onValueChange={summaryCheckboxChangeHandle}
            ref={summaryRef}
        />
    );

    if (item.isFinalNode) {
        return (
            <FlexNodeLabelContainer>
                {checkbox}
                <MultipleLeafLabel onClick={clickOnLabelHandle}>{labelName}</MultipleLeafLabel>
            </FlexNodeLabelContainer>
        );
    }

    return (
        <>
            <FlexNodeLabelContainer>
                <ArrowIcon opened={summaryOpened} onClick={toggleHandler} />
                {checkbox}
                <MultipleNodeLabel onClick={clickOnLabelHandle}>{labelName}</MultipleNodeLabel>
            </FlexNodeLabelContainer>
            {summaryOpened && (
                <FieldSet>
                    {children.map(childItem => (
                        <MultipleTreeNode
                            {...props}
                            key={childItem.elementId}
                            item={childItem}
                            parentState={summaryValue}
                            recalculateParentState={recalculateParentState}
                            updateNodesToSave={props.updateNodesToSave}
                            needNodesToOpen={props.needNodesToOpen}
                            nodesToSave={props.nodesToSave}
                            nestingLevel={props.nestingLevel + 1}
                        />
                    ))}
                    {showingPreloader && <Preloader />}
                </FieldSet>
            )}
        </>
    );
};
